/* eslint-disable no-barrel-files/no-barrel-files */
// TODO: De-barrel-ize
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { KVPhoto } from 'types'

import { PhotoReelThumbnail } from './PhotoReelThumbnail'
import { ScrollButton } from './ScrollButton'
import { ScrollContainer } from './ScrollContainer'

// Constants for thumbnail sizing - should match PhotoReelThumbnail.tsx
const THUMBNAIL_SIZE_PX = 128 // 8rem = 128px
const THUMBNAIL_GAP_PX = 8 // 0.5rem gap
const ITEM_WIDTH = THUMBNAIL_SIZE_PX + THUMBNAIL_GAP_PX

export {
  Content,
  EmptyText,
  ErrorText,
  LoadingText,
  PhotoPlaceholder,
  PhotoReelThumbnail,
  PhotosContainer,
  ScrollButton,
  ScrollContainer,
}

const Content = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const LoadingText = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-align: center;
  padding: 0.5rem;
  width: 100%;
`

const ErrorText = styled.div`
  color: ${({ theme }) => theme.forecast.danger};
  text-align: center;
  padding: 1rem;
  width: 100%;
`

const EmptyText = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-align: center;
  padding: 1rem;
  width: 100%;
`

const PhotosContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`

const PhotoPlaceholder = styled.div`
  width: ${THUMBNAIL_SIZE_PX}px;
  height: ${THUMBNAIL_SIZE_PX}px;
  flex-shrink: 0;
`

interface PhotoReelContentProps {
  photos: KVPhoto[]
  isLoading: boolean
  error: Error | null
  onPhotoClick: (id: string) => void
  highlightedImages?: string[]
  onPreviewClick?: (id: string) => void
}

export const PhotoReelContent: React.FC<PhotoReelContentProps> = ({
  photos,
  isLoading,
  error,
  onPhotoClick,
  highlightedImages,
  onPreviewClick,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 0 })

  // Calculate visible items based on scroll position
  const updateVisibleRange = useCallback(() => {
    if (!scrollContainerRef.current || photos.length === 0) return

    const container = scrollContainerRef.current
    const scrollLeft = container.scrollLeft
    const containerWidth = container.clientWidth

    // Buffer: render a few extra items on each side for smooth scrolling
    const buffer = 3

    // Calculate which items are visible
    const startIndex = Math.max(0, Math.floor(scrollLeft / ITEM_WIDTH) - buffer)
    const endIndex = Math.min(
      photos.length - 1,
      Math.ceil((scrollLeft + containerWidth) / ITEM_WIDTH) + buffer
    )

    setVisibleRange({ start: startIndex, end: endIndex })
  }, [photos.length])

  // Update visible range on scroll and resize
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const handleScroll = () => updateVisibleRange()

    updateVisibleRange() // Initial calculation
    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [updateVisibleRange])

  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' })
    }
  }

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' })
    }
  }

  const renderContent = () => {
    if (isLoading) {
      return <LoadingText>Loading photos...</LoadingText>
    }
    if (error) {
      return <ErrorText>Error loading photos</ErrorText>
    }
    if (photos.length === 0) {
      return <EmptyText>No photos found</EmptyText>
    }
    return (
      <PhotosContainer>
        {photos.map((photo, index) => {
          const isVisible =
            index >= visibleRange.start && index <= visibleRange.end
          return isVisible ? (
            <PhotoReelThumbnail
              key={photo.id}
              photo={photo}
              onClick={onPhotoClick}
              highlighted={highlightedImages?.includes(photo.id)}
              onPreviewClick={onPreviewClick}
            />
          ) : (
            <PhotoPlaceholder key={photo.id} />
          )
        })}
      </PhotosContainer>
    )
  }

  return (
    <Content>
      <ScrollButton
        direction='left'
        onClick={handleScrollLeft}
        style={{ maxWidth: '20px' }}
      />
      <ScrollContainer ref={scrollContainerRef}>
        {renderContent()}
      </ScrollContainer>
      <ScrollButton
        direction='right'
        onClick={handleScrollRight}
        style={{ maxWidth: '20px' }}
      />
    </Content>
  )
}
